'use client'

import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useDateLocale } from '@/modules/languages/lib/useDateLocale'
import { HomeCardImage } from './HomeCardImage'
import { LocaleLink } from '@/components/LocaleLink'
import { useCallback, useEffect, useState } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { DEFAULT_SITE_TIMEZONE } from '@/config/dateTimeOptions'
import { cn } from '@/utilities/ui'

import { externalArticleLink, SectionHeading, type SectionHeadingLevel } from './common'
import {
  HOME_BI_SECTION_HEADING,
  HOME_BI_X_CAROUSEL_CONTAINER,
  HOME_BI_X_CAROUSEL_SHELL,
  HOME_BI_X_CAROUSEL_TRACK,
  HOME_X_POST_TRANSLATED_TEXT,
} from './home-bi-layout'
import type { HomeXPostCardVM } from './types'

const CARD_BASIS =
  'basis-[calc((100cqw-0px)/1.08)] sm:basis-[calc((100cqw-1rem)/2)] lg:basis-[calc((100cqw-3rem)/4)]'

/**
 * 🔴 Fuso FIXO (React #418, 14/09/2026): este componente é de cliente, então a data sai duas vezes —
 * no servidor (UTC) e no navegador (fuso de quem lê). Sem `timeZone`, um post das 00:29 UTC virava
 * «08 set.» no HTML e «07 set.» em São Paulo, a hidratação falhava e o React refazia a página inteira.
 */
function formatPublishedAt(iso: string, locale: string): string {
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return ''
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: DEFAULT_SITE_TIMEZONE,
  }).format(new Date(ts))
}

function HomeXPostCard({
  post,
  showThumbnail = true,
  cardLinkLabel,
}: {
  post: HomeXPostCardVM
  showThumbnail?: boolean
  /** Rótulo do link de cada card (configurável no bloco). Vazio → `Home.viewMore` ("Ver mais") ("Ver mais"). */
  cardLinkLabel?: string
}) {
  const t = useTranslations('Home')
  const dateLocale = useDateLocale()
  return (
    <Card className="bg-card flex h-full min-w-0 flex-col gap-3 rounded-xl p-4 shadow-none ring-1 ring-foreground/10">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar className="size-9 shrink-0">
          {post.authorAvatar ? <AvatarImage src={post.authorAvatar} alt={post.authorName} /> : null}
          <AvatarFallback className="text-xs font-semibold">{post.authorInitials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight">{post.authorName}</p>
          <p className="text-muted-foreground truncate text-xs leading-tight">
            @{post.authorHandle}
          </p>
        </div>
      </div>

      <p className={HOME_X_POST_TRANSLATED_TEXT}>{post.text}</p>

      {showThumbnail && post.mediaSrc ? (
        <div className="relative aspect-16/10 w-full overflow-hidden rounded-(--post-thumb-radius,0px)">
          <HomeCardImage
            src={post.mediaPoster ?? post.mediaSrc}
            // Só para a IMAGEM do post (não para o poster de vídeo, que é outra mídia).
            srcSizes={post.mediaPoster ? undefined : post.mediaSizes}
            alt={post.mediaAlt ?? t('xPostMediaAlt', { author: post.authorName })}
            className="object-cover"
            sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 92vw"
          />
        </div>
      ) : null}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <time dateTime={post.publishedAt} className="text-muted-foreground text-xs">
          {formatPublishedAt(post.publishedAt, dateLocale)}
        </time>
        <LocaleLink
          {...externalArticleLink}
          href={post.href}
          target="_blank"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs font-semibold tracking-wide transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {cardLinkLabel?.trim() || t('viewMore')}
          <ArrowUpRight className="size-3" strokeWidth={2.25} aria-hidden />
        </LocaleLink>
      </div>
    </Card>
  )
}

export type HomeXPostsBandProps = {
  posts: HomeXPostCardVM[]
  sectionHeading?: string
  sectionHeadingLevel?: SectionHeadingLevel
  showSectionHeader?: boolean
  showMoreLink?: boolean
  moreTopicLabel?: string
  moreHref?: string
  headingId?: string
  showThumbnail?: boolean
  /** Rótulo do link de cada card (configurável no bloco). Vazio → `Home.viewMore` ("Ver mais"). */
  cardLinkLabel?: string
}

/** Faixa "Posts no X" — carrossel de 4 cards no desktop (sem vídeos). */
export function HomeXPostsBand({
  posts,
  sectionHeading,
  sectionHeadingLevel = 'h2',
  showSectionHeader = true,
  showMoreLink = true,
  moreTopicLabel = 'X',
  moreHref = 'https://x.com',
  headingId = 'home-videos-posts-heading',
  showThumbnail = true,
  cardLinkLabel,
}: HomeXPostsBandProps) {
  const t = useTranslations('Home')
  const resolvedHeading = sectionHeading ?? t('postsOnX')
  const [api, setApi] = useState<CarouselApi>()
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)
  const [snaps, setSnaps] = useState<number[]>([])
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    if (!api) return
    const sync = () => {
      setCanPrev(api.canScrollPrev())
      setCanNext(api.canScrollNext())
      setSnaps(api.scrollSnapList())
      setSelected(api.selectedScrollSnap())
    }
    sync()
    api.on('select', sync)
    api.on('reInit', sync)
    return () => {
      api.off('select', sync)
      api.off('reInit', sync)
    }
  }, [api])

  const scrollTo = useCallback((i: number) => api?.scrollTo(i), [api])

  if (!posts || posts.length === 0) return null

  return (
    <section
      data-home-section="home-videos"
      aria-labelledby={showSectionHeader ? headingId : undefined}
      className="min-w-0"
    >
      {showSectionHeader ? (
        <header className="section-heading mb-4 flex items-center justify-between gap-4 md:mb-5">
          <SectionHeading
            level={sectionHeadingLevel}
            id={headingId}
            className={cn('heading min-w-0 pr-3', HOME_BI_SECTION_HEADING)}
          >
            {resolvedHeading}
          </SectionHeading>
          {showMoreLink ? (
            <LocaleLink
              {...externalArticleLink}
              href={moreHref}
              target="_blank"
              data-component-type="cta-more-link-arrow"
              className="cta-more-link-arrow group inline-flex h-8 min-h-8 shrink-0 items-center gap-2 rounded-sm py-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              <span className="link-text text-xs leading-none font-semibold tracking-wide whitespace-nowrap">
                {t('viewMore')}
                {moreTopicLabel ? (
                  <span className="link-text-label">&nbsp;{moreTopicLabel}</span>
                ) : null}
              </span>
              <span
                className="icon-wrapper bg-foreground text-background flex size-6 shrink-0 items-center justify-center rounded-full transition-opacity group-hover:opacity-90"
                aria-hidden
              >
                <ArrowUpRight className="size-3" strokeWidth={2.25} />
              </span>
            </LocaleLink>
          ) : null}
        </header>
      ) : null}

      <div className={HOME_BI_X_CAROUSEL_SHELL}>
        <div className={HOME_BI_X_CAROUSEL_CONTAINER}>
          <Carousel
            setApi={setApi}
            opts={{ align: 'start', containScroll: 'trimSnaps' }}
            className={HOME_BI_X_CAROUSEL_TRACK}
          >
            <CarouselContent className="-ml-4">
              {posts.map((post) => (
                <CarouselItem key={post.id} className={cn('min-w-0 pl-4', CARD_BASIS)}>
                  <HomeXPostCard
                    post={post}
                    showThumbnail={showThumbnail}
                    cardLinkLabel={cardLinkLabel}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* Edge fade à direita (último card). */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-(--home-x-peek) bg-linear-to-l from-muted/40 to-transparent"
          />
        </div>
      </div>

      {snaps.length > 1 ? (
        <div className="mt-4 flex items-center justify-between gap-4 lg:mt-5">
          <div className="flex items-center" role="tablist" aria-label={t('postsNav')}>
            {snaps.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === selected}
                aria-label={t('goToGroup', { current: i + 1, total: snaps.length })}
                onClick={() => scrollTo(i)}
                // Área de toque de 24px (target-size); a bolinha visível continua com 8px.
                className="group flex h-6 min-w-6 items-center justify-center focus-visible:outline-none"
              >
                <span
                  aria-hidden
                  className={cn(
                    'h-2 rounded-full transition-all group-focus-visible:ring-2 group-focus-visible:ring-ring',
                    i === selected ? 'w-8 bg-foreground' : 'w-2 bg-muted-foreground/35',
                  )}
                />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 rounded-full"
              aria-label={t('prevPosts')}
              disabled={!canPrev}
              onClick={() => api?.scrollPrev()}
            >
              <ArrowLeft className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 rounded-full"
              aria-label={t('nextPosts')}
              disabled={!canNext}
              onClick={() => api?.scrollNext()}
            >
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
